import * as t from 'io-ts';

import {AccountDetailsPanelState, RAccountDetailsPanelStateDirty} from './accounts/accountDetailsPanelState';
import {AnalyticsPanelState, RAnalyticsPanelStateDirty} from './analytics/analyticsPanelState';
import {AppPanelState, RAppPanelState} from './app/appPanelState';
import {CalendarsPanelState, RCalendarsPanelState} from './calendars/calendarsPanelState';
import {CellsPanelState, RCellsPanelState} from './cells/cellsPanelState';
import {ComposerPanelState, RComposerPanelStateDirty} from './composer/composerPanelState';
import {
  ContactCardPanelState,
  RContactCardPanelStateDirty,
} from './contacts/contactCard/contactCardPanelState';
import {ContactDetailsPanelState, RContactDetailsPanelStateDirty} from './contacts/contactDetailsPanelState';
import {
  ContactsManagerPanelState,
  RContactsManagerPanelState,
} from './contacts/contactsManager/contactsManagerPanelState';
import {
  CrmPreviewPanelState,
  RCrmPreviewPanelStateDirty,
} from './contacts/contactsManager/crmPreview/crmPreviewState';
import {EmlPanelState, REmlPanelStateDirty} from './eml/emlPanelState';
import {InboxesPanelState, RInboxesPanelStateDirty} from './inboxes/inboxesPanelState';
import {LegacyPanelState, RLegacyPanelStateDirty} from './legacy/legacyPanelState';
import {MessageViewerPanelState, RMessageViewerPanelState} from './message/messageViewerPanelState';
import {PanelTypesEnum, RPanelId} from './panelStateBase';
import {RSettingsPanelStateDirty, SettingsPanelState} from './settings/settingsPanelState';
import {RViewerPanelState, ViewerPanelState} from './viewer/viewerPanelState';

/*
 * Runtime types.
 */

export const RPanelState = t.taggedUnion('type', [
  RAppPanelState,
  RInboxesPanelStateDirty,
  RCellsPanelState,
  RViewerPanelState,
  RMessageViewerPanelState,
  RComposerPanelStateDirty,
  RCalendarsPanelState,
  RAnalyticsPanelStateDirty,
  REmlPanelStateDirty,
  RContactDetailsPanelStateDirty,
  RContactsManagerPanelState,
  RSettingsPanelStateDirty,
  RLegacyPanelStateDirty,
  RAccountDetailsPanelStateDirty,
  RContactCardPanelStateDirty,
  RCrmPreviewPanelStateDirty,
]);
export const RPanelsState = t.record(RPanelId, RPanelState);

/*
 * Types.
 */

export type PanelState =
  | AppPanelState
  | InboxesPanelState
  | CellsPanelState
  | ViewerPanelState
  | MessageViewerPanelState
  | ComposerPanelState
  | CalendarsPanelState
  | AnalyticsPanelState
  | EmlPanelState
  | ContactDetailsPanelState
  | ContactsManagerPanelState
  | SettingsPanelState
  | LegacyPanelState
  | AccountDetailsPanelState
  | ContactCardPanelState
  | CrmPreviewPanelState;
export type PanelsState = {[T: string]: PanelState | undefined};

/*
 * Type guards.
 */

export function panelIsInboxesPanel(panel: PanelState | undefined): panel is InboxesPanelState {
  return panel?.type === PanelTypesEnum.INBOXES;
}

export function panelIsCellsPanel(panel: PanelState | undefined): panel is CellsPanelState {
  return panel?.type === PanelTypesEnum.CELLS;
}

export function panelIsViewerPanel(panel: PanelState | undefined): panel is ViewerPanelState {
  return panel?.type === PanelTypesEnum.VIEWER;
}

export function panelIsAnalyticsPanel(panel: PanelState | undefined): panel is AnalyticsPanelState {
  return panel?.type === PanelTypesEnum.ANALYTICS;
}

export function panelIsContactDetailsPanel(panel: PanelState | undefined): panel is ContactDetailsPanelState {
  return panel?.type === PanelTypesEnum.CONTACT_DETAILS;
}

export function panelIsContactsManagerPanel(
  panel: PanelState | undefined,
): panel is ContactsManagerPanelState {
  return panel?.type === PanelTypesEnum.CONTACTS_MANAGER;
}

export function panelIsComposerPanel(panel: PanelState | undefined): panel is ComposerPanelState {
  return panel?.type === PanelTypesEnum.COMPOSER;
}

export function panelIsAccountDetailsPanel(panel: PanelState | undefined): panel is AccountDetailsPanelState {
  return panel?.type === PanelTypesEnum.ACCOUNT_DETAILS;
}

export function panelIsSettingsPanel(panel: PanelState | undefined): panel is SettingsPanelState {
  return panel?.type === PanelTypesEnum.SETTINGS;
}

export function panelIsContactCardPanel(panel: PanelState | undefined): panel is ContactCardPanelState {
  return panel?.type === PanelTypesEnum.CONTACT_CARD;
}

export function panelIsCrmPreviewPanel(panel: PanelState | undefined): panel is CrmPreviewPanelState {
  return panel?.type === PanelTypesEnum.CRM_PREVIEW;
}
